import type { ComponentPropsWithRef, JSX } from 'react'
import { Link } from 'react-router-dom'

import { BufferIcon, Flex } from '@buffer-mono/popcorn'
import type { IconProps } from '@buffer-mono/popcorn'

import { useAccount } from '~publish/contexts/AccountContext'
import { useHasAccessToBufferHome } from '~publish/hooks/useHasAccessToBufferHome'

import { useAllChannelsUrl } from '../../hooks/useAllChannelsUrl'
import { useWeeklyPostingStreak } from '../Streaks'
import { StreakWidgetCount } from '../Streaks/StreaksWidget'
import { BufferLogoLink } from './BufferLogoLink'

import styles from './Logo.module.css'

function useLogoDestination(): { to: string; ariaLabel: string } {
  const allChannelsUrl = useAllChannelsUrl()
  const { hasAccess } = useHasAccessToBufferHome()
  if (hasAccess) {
    return { to: '/', ariaLabel: 'Go to home' }
  }
  return { to: allChannelsUrl, ariaLabel: 'Go to schedule' }
}

type LogoProps = Omit<ComponentPropsWithRef<'svg'>, 'to'>

export const Logo = (props: LogoProps): JSX.Element => {
  const { to, ariaLabel } = useLogoDestination()

  return <BufferLogoLink to={to} ariaLabel={ariaLabel} {...props} />
}

export const LogoIconWithStreakCount = (props: IconProps): JSX.Element => {
  const { account } = useAccount()
  const { to, ariaLabel } = useLogoDestination()

  const { data: { weeklyPostingStreak: streak } = {} } = useWeeklyPostingStreak(
    account?.currentOrganization?.id ?? undefined,
  )

  return (
    <Link to={to} aria-label={ariaLabel}>
      <Flex
        justify="center"
        align="center"
        fullWidth
        className={styles.logoIconWrapper}
      >
        <BufferIcon size="medium" color="default" {...props} />
        {streak && (
          <StreakWidgetCount streak={streak} className={styles.count} />
        )}
      </Flex>
    </Link>
  )
}
